import { Address } from "components/Address/Address";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { ExchangeRateInfoRow } from "components/design/ExchangeRateInfoRow";
import ToolTip from "components/design/toolTip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Modal from "components/DesignSystem/Modal/Modal";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { CopyIcon } from "components/icons/CopyIcon";
import { DownloadIcon } from "components/icons/Download";
import { LinkBreak } from "components/icons/LinkBreak";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { LinkedJournalEntry } from "components/JournalEntry/LinkedJournalEntry";
import { UnlinkTransactionModal } from "components/UnlinkTransactionModal/UnlinkTransactionModal";
import { AllBillExpenseScheduleDetails } from "./BillExpenseScheduleDetails";
import { BillActionsDropdown } from "./BillActionsDropdown";
import { BillAiSuggestionIndicator } from "./BillAiSuggestionIndicator";
import { WithholdingStatusTag } from "./WithholdingStatusTag";
import { DISPLAY_DATE } from "constants/date";
import {
  BILL_SOURCES_TYPE,
  BILL_STATUS,
  BILL_STATUS_LABEL,
  BILLS_SOURCES,
  FINANCIAL_RECORD_TYPE,
  TAG_TYPE,
} from "constants/vendorBills";
import { USD } from "constants/currency";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  useGetExchangeRateQuery,
  useUnlinkTransactionsMutation,
} from "store/apis/invoices";
import { useGetBillOrRequestQuery } from "store/apis/vendorBills";
import { openSlider } from "store/slices/transactions";
import { VendorBill } from "types/Models/vendorBills";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { formatDate } from "utils/formatDate";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

export const GridLayout = (props: React.HTMLAttributes<HTMLDivElement>) => {
  return <div {...props} className="t-grid t-grid-cols-2 t-gap-4" />;
};

const TransactionUnlinkButton = ({
  transactionId,
  billId,
}: {
  transactionId: string;
  billId: string;
}) => {
  const unlinkModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );

  if (!hasWritePermission) {
    return null;
  }

  return (
    <>
      <Button
        customType="ghost_icon"
        className="all:unset t-caret-text-30"
        onClick={(e) => {
          e.stopPropagation();
          unlinkModal.open();
        }}
      >
        <ToolTip text="Unlink Transaction">
          <span>
            <LinkBreak />
          </span>
        </ToolTip>
      </Button>

      {unlinkModal.isOpen && (
        <UnlinkTransactionModal
          isOpen={unlinkModal.isOpen}
          onClose={unlinkModal.close}
          invoiceId={billId}
          transactionId={transactionId}
          financialRecordType="BILL"
        />
      )}
    </>
  );
};

export const BillSlider = ({
  close,
  isOpen,
  billId,
}: ModalProps & { billId: string }) => {
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { copyToClipboard } = useCopyToClipboard();
  const dispatch = useDispatch();
  const { alertToast, successToast } = useToast();
  const { downloadFile, result: downloadState } = useDownloadFile();

  const { data, isLoading, isSuccess, isError } = useGetBillOrRequestQuery(
    { groupId, entityId, billId },
    {
      skip: !billId || !entityId || !groupId,
    }
  );

  const { data: exchangeRate } = useGetExchangeRateQuery(
    {
      base_currency: USD,
      target_currency: data?.currency!,
      date: data?.bill_date!,
    },
    { skip: !data || !data?.bill_date || data?.currency === USD }
  );

  const usdEquivalent = exchangeRate?.exchange_rate
    ? Number(data?.due_balance) / Number(exchangeRate.exchange_rate)
    : null;

  const unLinkTransactionModal = useModal();

  const [unlinkedTransaction, { isLoading: unlinkingTransaction }] =
    useUnlinkTransactionsMutation();

  const confirmUnlinkTransactions = async () => {
    if (!billId || !data) return;
    try {
      await unlinkedTransaction({
        groupId,
        entityId,
        invoiceId: billId,
        financial_record_type: "BILL",
      }).unwrap();
      successToast({
        message: "Transactions unlinked successfully",
      });
      unLinkTransactionModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to unlink transactions",
      });
    }
  };

  const isBill = data?.bill_document_type === FINANCIAL_RECORD_TYPE.BILL;
  const status = isBill ? data?.bill_payment_status : data?.bill_request_status;
  const isDraft = status === BILL_STATUS.DRAFT;
  const isPaidOrPartiallyPaid =
    status === BILL_STATUS.PAID || status === BILL_STATUS.PARTIALLY_PAID;

  const onClick = ({ bill }: { bill: VendorBill }) => {
    if (bill.bill_document_type === FINANCIAL_RECORD_TYPE.REQUEST) {
      history.push(
        link(`/books/bills-and-vendors/request/${bill.uuid}`, {
          moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.REQUEST },
        })
      );
      return;
    }
    if (bill.bill_document_type === FINANCIAL_RECORD_TYPE.BILL) {
      history.push(
        link(`/books/bills-and-vendors/bill/${bill.uuid}`, {
          moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL },
        })
      );
      return;
    }
  };

  const onEditBill = (bill: VendorBill) => {
    history.push(
      link(`/books/bills-and-vendors/bill/${bill.uuid}`, {
        moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL, editflow: "true" },
      })
    );
  };
  return (
    <>
      <Slider.Root open={isOpen} onOpenChange={close}>
        <Slider.Content useCustomOverlay>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            isEmpty={!data}
          >
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success>
              {data && (
                <>
                  <Slider.Header>
                    <Slider.Title className="t-flex t-gap-1 t-items-center">
                      {data.bill_number}
                      {status && (
                        <Tag tagType={TAG_TYPE[status]} rounded icon={false}>
                          {BILL_STATUS_LABEL[status]}
                        </Tag>
                      )}
                    </Slider.Title>
                    <div className="t-flex t-items-center t-gap-1">
                      <BillAiSuggestionIndicator bill={data} />
                      <BillActionsDropdown bill={data} isFromBillDetails />
                      <Slider.Close />
                    </div>
                  </Slider.Header>
                  <Tab.Root
                    defaultValue="DETAILS"
                    className="t-h-full t-flex t-flex-col t-min-h-0"
                  >
                    <div className="t-px-5 t-pt-3 t-pb-4 t-flex-shrink-0">
                      <Tab.List>
                        <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
                        <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
                      </Tab.List>
                    </div>
                    <Tab.Content
                      value="DETAILS"
                      className="t-flex t-flex-col t-flex-1 t-min-h-0 t-overflow-hidden"
                    >
                      <Slider.Body className="t-flex t-flex-col t-gap-4 t-overflow-y-auto t-max-h-full">
                        <Card.Root className="t-min-h-fit">
                          <Card.Header>
                            <Card.Title>Bill Details</Card.Title>
                          </Card.Header>
                          <Card.Body className="t-overflow-visible">
                            <GridLayout>
                              <InfoItem label="Title">
                                {data.title || "-"}
                              </InfoItem>
                              <InfoItem label="Source">
                                {BILLS_SOURCES[data.source as BILL_SOURCES_TYPE]
                                  ?.label || "-"}
                              </InfoItem>
                              <InfoItem label="Bill Date">
                                {data.bill_date
                                  ? dayjs(data.bill_date).format(DISPLAY_DATE)
                                  : "-"}
                              </InfoItem>
                              <InfoItem label="Due Date">
                                {data.due_date
                                  ? dayjs(data.due_date).format(DISPLAY_DATE)
                                  : "-"}
                              </InfoItem>

                              {data.currency && data.currency !== USD && (
                                <ExchangeRateInfoRow
                                  rate={exchangeRate?.exchange_rate}
                                  currency={data.currency}
                                  date={data.bill_date}
                                  usdEquivalent={usdEquivalent}
                                />
                              )}

                              <InfoItem label="Amount Paid">
                                {data.paid_amount ? (
                                  <AmountSuperScript
                                    amount={Number(data.paid_amount)}
                                  />
                                ) : (
                                  "-"
                                )}
                              </InfoItem>

                              {data.withholding_status && (
                                <InfoItem label="Withheld Amount">
                                  <div className="t-flex t-items-center t-gap-2">
                                    <AmountSuperScript
                                      amount={Number(data.withheld_amount || 0)}
                                    />
                                    <WithholdingStatusTag
                                      status={data.withholding_status}
                                    />
                                  </div>
                                </InfoItem>
                              )}

                              {data.withholding_status && (
                                <InfoItem label="Withholding Due Date">
                                  {data.withholding_due_date
                                    ? dayjs(data.withholding_due_date).format(
                                        DISPLAY_DATE
                                      )
                                    : "-"}
                                </InfoItem>
                              )}

                              <InfoItem label="Payment Link">
                                {data.payment_link ? (
                                  <div className="t-flex t-gap-1">
                                    <div className="t-truncate">
                                      {data.payment_link}
                                    </div>
                                    <Button
                                      customType="link"
                                      size="small"
                                      onClick={() =>
                                        copyToClipboard(data.payment_link)
                                      }
                                    >
                                      <CopyIcon />
                                    </Button>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </InfoItem>
                            </GridLayout>
                          </Card.Body>
                        </Card.Root>

                        <AllBillExpenseScheduleDetails bill={data} />

                        <Card.Root className="t-min-h-fit">
                          <Card.Header>
                            <Card.Title>Vendor Details</Card.Title>
                          </Card.Header>
                          <Card.Body className="t-gap-4 t-flex t-flex-col t-overflow-visible">
                            <GridLayout>
                              <InfoItem label="Name">
                                {data.entity_merchant_data?.name || "-"}
                              </InfoItem>
                              <InfoItem label="Email">
                                {data.entity_merchant_data?.email ? (
                                  <div className="t-flex t-gap-1">
                                    <div className="t-truncate">
                                      {data.entity_merchant_data.email}
                                    </div>
                                    <Button
                                      customType="link"
                                      size="small"
                                      onClick={() =>
                                        copyToClipboard(
                                          data.entity_merchant_data?.email!
                                        )
                                      }
                                    >
                                      <CopyIcon />
                                    </Button>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </InfoItem>

                              <InfoItem label="Address">
                                {data.merchant_address ? (
                                  <Address address={data.merchant_address} />
                                ) : (
                                  "-"
                                )}
                              </InfoItem>
                            </GridLayout>

                            <InfoItem label="Payment Instructions">
                              {data.payment_instructions ? (
                                <div className="t-break-words">
                                  {data.payment_instructions}
                                </div>
                              ) : (
                                "-"
                              )}
                            </InfoItem>
                          </Card.Body>
                        </Card.Root>
                        {(data.forwarded_by || data.forwarded_at) && (
                          <Card.Root className="t-min-h-fit">
                            <Card.Header>
                              <Card.Title>Bill forward details</Card.Title>
                            </Card.Header>
                            <Card.Body className="t-gap-4 t-flex t-flex-col t-overflow-visible">
                              <GridLayout>
                                <InfoItem label="Forward date">
                                  {dayjs(data.forwarded_at).format(
                                    DISPLAY_DATE
                                  ) || "-"}
                                </InfoItem>
                                <InfoItem label="Forwarded by">
                                  {data.forwarded_by ? (
                                    <div className="t-flex t-gap-1">
                                      <div className="t-truncate">
                                        {data.forwarded_by}
                                      </div>
                                      <Button
                                        customType="link"
                                        size="small"
                                        onClick={() =>
                                          copyToClipboard(data.forwarded_by!)
                                        }
                                      >
                                        <CopyIcon />
                                      </Button>
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </InfoItem>
                              </GridLayout>
                              {data.bill_document && (
                                <InfoItem label="Original bill attachment from email">
                                  <div className="t-text-body-sm t-flex-1">
                                    <FilePreviewTag
                                      file={data.bill_document}
                                      size="small"
                                      block
                                    >
                                      <Button
                                        customType="ghost_icon"
                                        size="xsmall"
                                        disabled={downloadState.isFetching}
                                        isLoading={downloadState.isFetching}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadFile({
                                            file_id: data.bill_document!.uuid,
                                            group_id: groupId,
                                          });
                                        }}
                                      >
                                        <DownloadIcon color="currentColor" />
                                      </Button>
                                    </FilePreviewTag>
                                  </div>
                                </InfoItem>
                              )}
                            </Card.Body>
                          </Card.Root>
                        )}

                        {data?.associated_txns &&
                          data?.associated_txns?.length > 0 && (
                            <>
                              <Accordion.Root
                                defaultValue={["TRANSACTION_DETAILS"]}
                                type="multiple"
                              >
                                <Accordion.Item value="TRANSACTION_DETAILS">
                                  <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                                    <div className="t-flex t-justify-between t-w-full">
                                      <span>Linked Transactions</span>
                                    </div>
                                    <LockedUi
                                      feature={WRITE_PERMISSION_KEYS.EXPENSE}
                                    >
                                      <div
                                        className="t-flex t-gap-1.5 t-text-purple t-items-center t-text-button-sm t-cursor-pointer t-w-20"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          unLinkTransactionModal.open();
                                        }}
                                      >
                                        Unlink all
                                      </div>
                                    </LockedUi>
                                  </Accordion.Trigger>
                                  <Accordion.Content className="t-flex t-flex-col t-gap-6">
                                    {data.associated_txns?.map(
                                      ({ amount, date, uuid, source }) => (
                                        <button
                                          className="t-text-subtext all:unset"
                                          key={uuid}
                                          onClick={() =>
                                            dispatch(openSlider(uuid))
                                          }
                                        >
                                          <div className="t-grid t-grid-cols-6">
                                            <div className="t-col-span-4 t-flex t-gap-2">
                                              <Avatar
                                                src={""}
                                                alt={
                                                  data.entity_merchant_data
                                                    ?.name || ""
                                                }
                                              />
                                              <div>
                                                <div className="">
                                                  {
                                                    data.entity_merchant_data
                                                      ?.name
                                                  }
                                                </div>
                                                <div className="t-flex t-gap-2 t-text-body-sm t-text-text-30 ">
                                                  <div className="t-text-body-sm t-text-text-30">
                                                    {formatDate(date)}
                                                  </div>{" "}
                                                  <span className="t-text-neutral-20">
                                                    •
                                                  </span>
                                                  <div className="first-letter:t-uppercase t-lowercase">
                                                    {source}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="t-col-span-2 t-justify-self-end t-flex t-justify-end">
                                              <div className="t-text-subtext t-flex t-gap-4 t-items-center">
                                                <AmountSuperScript
                                                  amount={Number(amount)}
                                                />
                                                <Divider
                                                  fullHeight
                                                  orientation="vertical"
                                                />
                                                <TransactionUnlinkButton
                                                  transactionId={uuid}
                                                  billId={billId}
                                                />
                                                <span className="t-flex t-text-text-30">
                                                  <SmallArrowUpRight />
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </button>
                                      )
                                    )}
                                  </Accordion.Content>
                                </Accordion.Item>
                              </Accordion.Root>

                              <Modal.Root
                                open={unLinkTransactionModal.isOpen}
                                onOpenChange={unLinkTransactionModal.close}
                              >
                                <Modal.Content size="small">
                                  <Modal.Body>
                                    <div>
                                      <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                                        <TriangleWarning />
                                      </span>
                                      <p className="t-mb-2 t-text-subtext t-mt-5">
                                        Unlink all transactions?
                                      </p>
                                      <div className="t-text-body-sm t-text-text-30">
                                        Unlinking the transactions may change
                                        the bill status and will update the
                                        corresponding transaction's
                                        categorisation.
                                      </div>
                                    </div>
                                  </Modal.Body>
                                  <Modal.FooterButtonGroup>
                                    <Modal.RawClose asChild>
                                      <Button>Cancel</Button>
                                    </Modal.RawClose>
                                    <Button
                                      isLoading={unlinkingTransaction}
                                      disabled={unlinkingTransaction}
                                      customType="primary"
                                      onClick={confirmUnlinkTransactions}
                                    >
                                      Confirm
                                    </Button>
                                  </Modal.FooterButtonGroup>
                                </Modal.Content>
                              </Modal.Root>
                            </>
                          )}
                        {data?.ledger_entry?.uuid && (
                          <LinkedJournalEntry
                            ledgerEntryIds={[data.ledger_entry.uuid]}
                            entityId={entityId}
                          />
                        )}
                      </Slider.Body>
                      {(isBill || isDraft) && !isPaidOrPartiallyPaid && (
                        <Slider.FooterButtonGroup>
                          <LockedUi
                            feature={WRITE_PERMISSION_KEYS.EXPENSE}
                            blockedUi={<LockedButton text="Edit Bill" />}
                          >
                            <Button
                              customType="primary"
                              onClick={() =>
                                isDraft
                                  ? onClick({ bill: data })
                                  : onEditBill(data)
                              }
                            >
                              Edit Bill
                            </Button>
                          </LockedUi>
                        </Slider.FooterButtonGroup>
                      )}
                    </Tab.Content>
                    <Tab.Content
                      value="ACTIVITY"
                      className="t-flex-1 t-min-h-0 t-overflow-hidden"
                    >
                      <AuditlogList entityId={entityId} objectId={billId} />
                    </Tab.Content>
                  </Tab.Root>
                </>
              )}
            </Async.Success>
          </Async.Root>
        </Slider.Content>
      </Slider.Root>

      <TransactionSlider />
    </>
  );
};
